import React, { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '../../lib/supabase'
import { fmtMoney, fmtDateShort as fmtDate } from '../../utils/formatters'
import { buildMailto, buildSmsUrl, logFollowUpAttempt, markContacted } from '../../lib/followUpLinks'

var SERVICE_TYPES = [
  'Inground Install', 'Above Ground Install', 'Liner Replacement',
  'Opening', 'Closing', 'Service Call', 'Repair', 'Other',
]
var SERVICE_STATUSES = ['completed', 'pending', 'scheduled']
var CUSTOMER_TYPES = [
  { value: 'prospect', label: 'Prospect' },
  { value: 'customer', label: 'Customer' },
  { value: 'past', label: 'Past Customer' },
]
var POOL_TYPES = [
  { value: '', label: 'None yet' },
  { value: 'above_ground', label: 'Above Ground' },
  { value: 'inground_residential', label: 'Inground — Residential' },
  { value: 'inground_commercial', label: 'Inground — Commercial' },
]

function normalizeKey(name, phone) {
  var n = (name || '').toLowerCase().replace(/\s+/g, ' ').trim()
  var p = (phone || '').replace(/\D/g, '')
  return n + '|' + p
}

function findCustomerByName(existing, name) {
  var target = (name || '').toLowerCase().replace(/\s+/g, ' ').trim()
  if (!target) return null
  var match = (existing || []).find(function(c) {
    return (c.client_name || '').toLowerCase().replace(/\s+/g, ' ').trim() === target
  })
  return match ? match.id : null
}

// Backfill any store_requests for this store that don't yet have a customer_id.
// Match existing customers by normalized (name, phone); create via upsert if
// absent; then link each request via the link_request_to_customer RPC so the
// UPDATE succeeds despite the tight store_requests_update RLS (which otherwise
// silently drops store_partner UPDATEs and makes the backfill look successful
// while doing nothing).
async function doBackfill(userId, profile) {
  // eslint-disable-next-line no-console
  console.log('[CustomersTab] doBackfill start for', userId)
  var { data: orphanReqs, error: orphanErr } = await supabase.from('store_requests')
    .select('id, client_name, client_phone, client_email, site_address, pool_type, pool_size, store_name')
    .eq('store_user_id', userId).is('customer_id', null)
  if (orphanErr) { console.error('[CustomersTab] Backfill fetch orphans failed', orphanErr); return false }
  // eslint-disable-next-line no-console
  console.log('[CustomersTab] Backfill orphan count:', orphanReqs?.length || 0)
  if (!orphanReqs || orphanReqs.length === 0) return false

  var { data: existing, error: existErr } = await supabase.from('store_customers')
    .select('id, client_name, client_phone').eq('store_user_id', userId)
  if (existErr) { console.error('[CustomersTab] Backfill fetch existing customers failed', existErr); return false }
  var byKey = {}
  ;(existing || []).forEach(function(c) { byKey[normalizeKey(c.client_name, c.client_phone)] = c.id })

  var updates = []
  for (var i = 0; i < orphanReqs.length; i++) {
    var req = orphanReqs[i]
    var key = normalizeKey(req.client_name, req.client_phone)
    var customerId = byKey[key]
    if (!customerId) {
      // Name-only fallback — same person with / without phone on file.
      customerId = findCustomerByName(existing, req.client_name)
    }
    if (!customerId) {
      var upsertRes = await supabase.from('store_customers').upsert({
        store_user_id: userId,
        store_name: req.store_name || (profile && (profile.store_name || profile.full_name)) || null,
        client_name: req.client_name || 'Unnamed',
        client_phone: req.client_phone || null,
        client_email: req.client_email || null,
        site_address: req.site_address || null,
        pool_type: req.pool_type || null,
        pool_size: req.pool_size || null,
        customer_type: 'customer',
        status: 'active',
      }, { onConflict: 'store_user_id,client_name' }).select('id').single()
      if (upsertRes.error || !upsertRes.data) {
        console.error('[CustomersTab] Backfill upsert customer failed for', req.client_name, upsertRes.error)
        continue
      }
      customerId = upsertRes.data.id
      byKey[key] = customerId
    }
    updates.push({ id: req.id, customer_id: customerId, client_name: req.client_name })
  }

  var linked = 0
  var skipped = 0
  for (var j = 0; j < updates.length; j++) {
    var u = updates[j]
    // RPC runs SECURITY DEFINER, so a store_partner can set customer_id on
    // their own orphan request despite the tight UPDATE policy. Returns the
    // UPDATE row count — a 0 here is the bug we were silently hitting before.
    var { data: rowCount, error: rpcErr } = await supabase.rpc('link_request_to_customer', {
      p_request_id: u.id,
      p_customer_id: u.customer_id,
    })
    if (rpcErr) {
      console.error('[CustomersTab] Backfill link RPC failed for', u.id, '(', u.client_name, ')', rpcErr)
      continue
    }
    if (!rowCount || rowCount === 0) {
      skipped++
      // eslint-disable-next-line no-console
      console.warn('[CustomersTab] Backfill link rpc affected 0 rows for', u.id, '(', u.client_name, ') — permissions?')
      continue
    }
    linked++
    // eslint-disable-next-line no-console
    console.log('[CustomersTab] Backfill linked request', u.id, '→ customer', u.customer_id, '(', u.client_name, ')')
  }
  // eslint-disable-next-line no-console
  console.log('[CustomersTab] doBackfill done — linked:', linked, 'skipped:', skipped, 'total orphans:', orphanReqs.length)
  return linked > 0
}

export default function CustomersTab({ userId, profile, myJobs, onRefresh, customerNav }) {
  var renderCountRef = useRef(0)
  renderCountRef.current += 1
  var [customers, setCustomers] = useState([])
  var [loading, setLoading] = useState(true)
  // Diagnostic — rapid-fire log here = re-render loop still present. Includes
  // prop identity markers so we can see which parent prop (if any) churned.
  // eslint-disable-next-line no-console
  console.log('[CustomersTab] render #' + renderCountRef.current, {
    userId: userId,
    customers: customers.length,
    loading: loading,
    myJobsLen: (myJobs || []).length,
    profileIdentity: profile ? profile.id : null,
    onRefreshIdentity: onRefresh ? onRefresh.name || 'fn' : null,
  })
  var [search, setSearch] = useState('')
  var [filter, setFilter] = useState('all')
  var [sortBy, setSortBy] = useState('recent')
  var [selectedId, setSelectedId] = useState(null)
  var [detailTab, setDetailTab] = useState('services')

  var [showAdd, setShowAdd] = useState(false)
  var [addForm, setAddForm] = useState({})
  var [addSaving, setAddSaving] = useState(false)
  var [addError, setAddError] = useState(null)

  var [services, setServices] = useState([])
  var [emailLogs, setEmailLogs] = useState([])
  var [loadingDetail, setLoadingDetail] = useState(false)

  var [showService, setShowService] = useState(null) // { mode, record? }
  var [serviceForm, setServiceForm] = useState({})
  var [serviceSaving, setServiceSaving] = useState(false)

  var [showEmail, setShowEmail] = useState(null)
  var [emailSubject, setEmailSubject] = useState('')
  var [emailBody, setEmailBody] = useState('')
  var [emailOpening, setEmailOpening] = useState(false)

  var [notesDraft, setNotesDraft] = useState('')
  var [savingField, setSavingField] = useState(null)
  var [newTagInput, setNewTagInput] = useState('')

  // Keep latest profile/onRefresh in refs so the mount-only init effect
  // never needs to re-run just because the parent re-rendered with new
  // object references (AuthContext's profile is recreated every render).
  var profileRef = useRef(profile)
  var onRefreshRef = useRef(onRefresh)
  useEffect(function() {
    // eslint-disable-next-line no-console
    console.log('[CustomersTab] effect: stash profile ref')
    profileRef.current = profile
  }, [profile])
  useEffect(function() {
    // eslint-disable-next-line no-console
    console.log('[CustomersTab] effect: stash onRefresh ref')
    onRefreshRef.current = onRefresh
  }, [onRefresh])

  var fetchCustomers = async function() {
    var { data, error } = await supabase.from('store_customers').select('*')
      .eq('store_user_id', userId).order('created_at', { ascending: false })
    if (error) { console.error('[CustomersTab] fetchCustomers failed:', error); return [] }
    // eslint-disable-next-line no-console
    console.log('[CustomersTab] setCustomers called with', (data || []).length, 'items')
    setCustomers(data || [])
    return data || []
  }

  // Mount-only init: runs backfill once, fetches customers, refreshes parent
  // if any requests got linked. Ref-guarded so the effect can never spin.
  var didInitRef = useRef(false)
  useEffect(function() {
    // eslint-disable-next-line no-console
    console.log('[CustomersTab] effect: mount init (already-ran?)', didInitRef.current, 'userId:', userId)
    if (didInitRef.current) return
    didInitRef.current = true
    ;(async function() {
      var backfilled = false
      try {
        backfilled = await doBackfill(userId, profileRef.current)
      } catch (e) {
        console.error('[CustomersTab] Backfill error:', e)
      }
      try {
        await fetchCustomers()
      } catch (e) {
        console.error('[CustomersTab] fetchCustomers error:', e)
      } finally {
        // eslint-disable-next-line no-console
        console.log('[CustomersTab] setLoading(false) after init')
        setLoading(false)
      }
      if (backfilled && onRefreshRef.current) onRefreshRef.current()
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  var selected = useMemo(function() {
    return customers.find(function(c) { return c.id === selectedId }) || null
  }, [customers, selectedId])

  // External nav — StorePortalPage sets customerNav = { id, bump } when the
  // user taps VIEW CUSTOMER on a request card. We jump into the detail view
  // and default to the Open Requests sub-tab.
  useEffect(function() {
    if (!customerNav || !customerNav.id) return
    setSelectedId(customerNav.id)
    setDetailTab('requests')
  }, [customerNav])

  // Load service history + email logs when a customer is selected
  useEffect(function() {
    // eslint-disable-next-line no-console
    console.log('[CustomersTab] effect: load detail for selectedId', selectedId)
    if (!selectedId) { setServices([]); setEmailLogs([]); return }
    setLoadingDetail(true)
    ;(async function() {
      var [svcRes, logRes] = await Promise.all([
        supabase.from('store_customer_services').select('*')
          .eq('customer_id', selectedId).order('service_date', { ascending: false }),
        supabase.from('store_email_logs').select('*')
          .eq('customer_id', selectedId).order('sent_at', { ascending: false }),
      ])
      setServices(svcRes.data || [])
      setEmailLogs(logRes.data || [])
      setLoadingDetail(false)
    })()
  }, [selectedId])

  // Sync notes draft when selected customer changes
  useEffect(function() {
    // eslint-disable-next-line no-console
    console.log('[CustomersTab] effect: reset notes/tag/tab for', selectedId)
    setNotesDraft(selected?.notes || '')
    setNewTagInput('')
    setDetailTab('services')
  }, [selectedId]) // eslint-disable-line react-hooks/exhaustive-deps

  // --- Filtering + sorting ---
  var openRequestsByCustomerId = useMemo(function() {
    var map = {}
    ;(myJobs || []).forEach(function(r) {
      if (r.status !== 'completed' && r.customer_id) {
        if (!map[r.customer_id]) map[r.customer_id] = []
        map[r.customer_id].push(r)
      }
    })
    return map
  }, [myJobs])

  var requestsByName = useMemo(function() {
    // Fallback match by client_name for requests not yet linked (pre-backfill race)
    var map = {}
    ;(myJobs || []).forEach(function(r) {
      if (r.status !== 'completed' && !r.customer_id && r.client_name) {
        var k = r.client_name.toLowerCase().trim()
        if (!map[k]) map[k] = []
        map[k].push(r)
      }
    })
    return map
  }, [myJobs])

  var getOpenRequests = function(customer) {
    if (!customer) return []
    var byId = openRequestsByCustomerId[customer.id] || []
    var byName = requestsByName[(customer.client_name || '').toLowerCase().trim()] || []
    return byId.concat(byName)
  }

  var visibleCustomers = useMemo(function() {
    var list = customers.slice()
    if (search) {
      var t = search.toLowerCase()
      list = list.filter(function(c) {
        return [c.client_name, c.client_email, c.client_phone, c.site_address]
          .some(function(f) { return f && String(f).toLowerCase().includes(t) })
      })
    }
    if (filter === 'active') list = list.filter(function(c) { return c.status !== 'inactive' && c.customer_type !== 'past' })
    else if (filter === 'prospects') list = list.filter(function(c) { return c.customer_type === 'prospect' })
    else if (filter === 'past') list = list.filter(function(c) { return c.customer_type === 'past' })
    else if (filter === 'open_requests') list = list.filter(function(c) { return getOpenRequests(c).length > 0 })

    if (sortBy === 'name') list.sort(function(a, b) { return (a.client_name || '').localeCompare(b.client_name || '') })
    else if (sortBy === 'contact') list.sort(function(a, b) { return (b.last_contact_date || '').localeCompare(a.last_contact_date || '') })
    // recent (default) uses fetch order
    return list
  }, [customers, search, filter, sortBy, openRequestsByCustomerId, requestsByName]) // eslint-disable-line react-hooks/exhaustive-deps

  // --- Add customer ---
  var handleAddCustomer = async function() {
    console.log('Add customer submit:', addForm)
    setAddError(null)
    if (!addForm.client_name || !addForm.client_name.trim()) { setAddError('Name is required.'); return }
    setAddSaving(true)
    try {
      var tags = (addForm.tags_input || '').split(',').map(function(s) { return s.trim() }).filter(Boolean)
      var { data, error } = await supabase.from('store_customers').insert({
        store_user_id: userId,
        store_name: profile?.store_name || profile?.full_name || null,
        client_name: addForm.client_name.trim(),
        client_phone: addForm.client_phone || null,
        client_email: addForm.client_email || null,
        site_address: addForm.site_address || null,
        pool_type: addForm.pool_type || null,
        pool_size: addForm.pool_size || null,
        pool_install_date: addForm.pool_install_date || null,
        customer_type: addForm.customer_type || 'customer',
        status: 'active',
        tags: tags.length > 0 ? tags : null,
        notes: addForm.notes || null,
      }).select().single()
      if (error) { console.error('Add customer failed:', error); setAddError(error.message); setAddSaving(false); return }
      setCustomers(function(prev) { return [data].concat(prev) })
      setShowAdd(false)
      setAddForm({})
      setSelectedId(data.id)
    } catch (e) {
      setAddError((e && e.message) || String(e))
    }
    setAddSaving(false)
  }

  // --- Inline updates on customer detail ---
  var updateCustomer = async function(patch) {
    if (!selected) return
    setSavingField(Object.keys(patch)[0])
    var { data, error } = await supabase.from('store_customers').update(patch).eq('id', selected.id).select().single()
    if (error) { alert('Update failed: ' + error.message); setSavingField(null); return }
    setCustomers(function(prev) { return prev.map(function(c) { return c.id === data.id ? data : c }) })
    setSavingField(null)
  }

  var saveNotes = function() {
    if ((selected?.notes || '') === notesDraft) return
    updateCustomer({ notes: notesDraft || null })
  }

  var addTag = function() {
    if (!newTagInput.trim() || !selected) return
    var tag = newTagInput.trim()
    var existing = selected.tags || []
    if (existing.includes(tag)) { setNewTagInput(''); return }
    updateCustomer({ tags: existing.concat([tag]) })
    setNewTagInput('')
  }
  var removeTag = function(tag) {
    if (!selected) return
    var next = (selected.tags || []).filter(function(t) { return t !== tag })
    updateCustomer({ tags: next.length > 0 ? next : null })
  }

  // --- Service CRUD ---
  var openAddService = function() {
    setServiceForm({
      service_type: 'Service Call',
      service_date: new Date().toISOString().split('T')[0],
      pool_size: selected?.pool_size || '',
      pool_type: selected?.pool_type || '',
      description: '',
      installer: '',
      total_amount: '',
      status: 'completed',
      notes: '',
    })
    setShowService({ mode: 'add' })
  }
  var openEditService = function(record) {
    setServiceForm({
      service_type: record.service_type || 'Other',
      service_date: record.service_date || new Date().toISOString().split('T')[0],
      pool_size: record.pool_size || '',
      pool_type: record.pool_type || '',
      description: record.description || '',
      installer: record.installer || '',
      total_amount: record.total_amount != null ? String(record.total_amount) : '',
      status: record.status || 'completed',
      notes: record.notes || '',
    })
    setShowService({ mode: 'edit', record: record })
  }
  var saveService = async function() {
    if (!selected) return
    console.log('Save service:', serviceForm, 'mode:', showService?.mode)
    setServiceSaving(true)
    var payload = {
      service_type: serviceForm.service_type,
      service_date: serviceForm.service_date || null,
      pool_size: serviceForm.pool_size || null,
      pool_type: serviceForm.pool_type || null,
      description: serviceForm.description || null,
      installer: serviceForm.installer || null,
      total_amount: serviceForm.total_amount !== '' ? parseFloat(serviceForm.total_amount) : null,
      status: serviceForm.status || 'completed',
      notes: serviceForm.notes || null,
    }
    try {
      if (showService.mode === 'edit' && showService.record) {
        var { data: upd, error: uErr } = await supabase.from('store_customer_services')
          .update(payload).eq('id', showService.record.id).select().single()
        if (uErr) { alert('Failed to save service: ' + uErr.message); setServiceSaving(false); return }
        setServices(function(prev) { return prev.map(function(s) { return s.id === upd.id ? upd : s }) })
      } else {
        var { data: ins, error: iErr } = await supabase.from('store_customer_services').insert(Object.assign({}, payload, {
          customer_id: selected.id,
          store_user_id: userId,
        })).select().single()
        if (iErr) { alert('Failed to add service: ' + iErr.message); setServiceSaving(false); return }
        setServices(function(prev) { return [ins].concat(prev) })
      }
      setShowService(null)
      setServiceForm({})
    } catch (e) {
      alert('Error saving service: ' + ((e && e.message) || e))
    }
    setServiceSaving(false)
  }
  var deleteService = async function(record) {
    if (!window.confirm('Delete this service entry? This cannot be undone.')) return
    var { error } = await supabase.from('store_customer_services').delete().eq('id', record.id)
    if (error) { alert('Delete failed: ' + error.message); return }
    setServices(function(prev) { return prev.filter(function(s) { return s.id !== record.id }) })
  }

  // --- Send follow-up via the user's own email app (mailto:) ---
  var openEmail = function(customer) {
    if (!customer.client_email) { alert('No email on file for this customer.'); return }
    setEmailSubject('Follow-up from ' + (profile?.store_name || 'CP Quality Pools'))
    setEmailBody('Hi ' + ((customer.client_name || '').split(' ')[0] || 'there') + ',\n\nI wanted to follow up on your pool project. Please let me know if you have any questions or if you\'d like to move forward.\n\nThanks,\n' + (profile?.full_name || '') + '\n' + (profile?.store_name || ''))
    setShowEmail(customer)
  }

  var openInEmailApp = async function() {
    if (!showEmail) return
    setEmailOpening(true)
    try {
      var customer = showEmail
      var today = new Date().toISOString().split('T')[0]
      // Log the ATTEMPT before navigating. We can't confirm send once the user
      // hops over to Gmail/Mail — this records what they composed in our modal.
      await logFollowUpAttempt({
        storeUserId: userId,
        customerId: customer.id,
        clientName: customer.client_name,
        clientEmail: customer.client_email,
        subject: emailSubject,
        body: emailBody,
        sendMethod: 'mailto',
      })
      await markContacted({ customerId: customer.id })

      // Optimistic UI update so the "Last contacted" label moves immediately.
      setCustomers(function(prev) { return prev.map(function(c) {
        return c.id === customer.id ? Object.assign({}, c, { last_contact_date: today }) : c
      }) })
      if (selectedId === customer.id) {
        // Refresh email logs so the attempt appears in history without a reload.
        var { data: logs } = await supabase.from('store_email_logs').select('*')
          .eq('customer_id', customer.id).order('sent_at', { ascending: false })
        setEmailLogs(logs || [])
      }
      setShowEmail(null)
      // Hand off to the OS. This navigates the page — do it last.
      window.location.href = buildMailto({
        to: customer.client_email,
        subject: emailSubject,
        body: emailBody,
      })
    } catch (e) {
      alert('Could not open email app: ' + ((e && e.message) || e))
    }
    setEmailOpening(false)
  }

  var openInSmsApp = async function(customer) {
    if (!customer || !customer.client_phone) { alert('No phone on file for this customer.'); return }
    var partnerName = profile?.full_name || ''
    var storeName = profile?.store_name || 'our team'
    var sizeBlurb = customer.pool_size ? customer.pool_size + ' ' : ''
    var typeBlurb = customer.pool_type ? (customer.pool_type + ' ').replace(/_/g, ' ') : ''
    var message = 'Hi ' + ((customer.client_name || '').split(' ')[0] || 'there') +
      ', this is ' + partnerName + ' from ' + storeName +
      '. Just checking in on your ' + sizeBlurb + typeBlurb + 'pool request \u2014 are you still interested?'
    try {
      await logFollowUpAttempt({
        storeUserId: userId,
        customerId: customer.id,
        clientName: customer.client_name,
        subject: 'SMS follow-up',
        body: message,
        sendMethod: 'sms',
      })
      await markContacted({ customerId: customer.id })
      setCustomers(function(prev) { return prev.map(function(c) {
        return c.id === customer.id ? Object.assign({}, c, { last_contact_date: new Date().toISOString().split('T')[0] }) : c
      }) })
      if (selectedId === customer.id) {
        var { data: logs } = await supabase.from('store_email_logs').select('*')
          .eq('customer_id', customer.id).order('sent_at', { ascending: false })
        setEmailLogs(logs || [])
      }
      window.location.href = buildSmsUrl(customer.client_phone, message)
    } catch (e) {
      alert('Could not open messaging app: ' + ((e && e.message) || e))
    }
  }

  // Intentionally no full-screen loading blocker. The tab shell (search, chips,
  // add button) stays mounted during the backfill + initial fetch so the UI
  // doesn't visibly disappear and reappear. A subtle loading row replaces the
  // list body while customers is still empty.

  // === DETAIL VIEW ===
  if (selected) {
    var openReqs = getOpenRequests(selected)
    return (
      <div>
        <button type="button" onClick={function() { setSelectedId(null) }} style={st.backBtn}>&larr; BACK TO CUSTOMERS</button>

        <div style={st.detailHeader}>
          <div style={{ flex: 1 }}>
            <div style={st.detailName}>{selected.client_name}</div>
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginTop: '6px' }}>
              {selected.client_phone && <a href={'tel:' + selected.client_phone} style={st.contactLink}>{String.fromCodePoint(0x1F4DE)} {selected.client_phone}</a>}
              {selected.client_email && <a href={'mailto:' + selected.client_email} style={st.contactLink}>{String.fromCodePoint(0x2709, 0xFE0F)} {selected.client_email}</a>}
              {selected.site_address && <a href={'https://maps.google.com/?q=' + encodeURIComponent(selected.site_address)} target="_blank" rel="noreferrer" style={st.contactLink}>{String.fromCodePoint(0x1F4CD)} {selected.site_address}</a>}
            </div>
            {selected.last_contact_date && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)', marginTop: '6px' }}>Last contacted: {fmtDate(selected.last_contact_date)}</div>}
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {selected.client_phone && (
              <button type="button" onClick={function() { openInSmsApp(selected) }} style={st.ghostBtn}>SEND TEXT</button>
            )}
            {selected.client_email && (
              <button type="button" onClick={function() { openEmail(selected) }} style={st.primaryBtn}>SEND EMAIL</button>
            )}
          </div>
        </div>

        {/* Pool info + type + tags */}
        <div style={st.infoGrid}>
          <div style={st.field}>
            <div style={st.formLabel}>CUSTOMER TYPE</div>
            <select value={selected.customer_type || 'customer'} onChange={function(e) { updateCustomer({ customer_type: e.target.value }) }} style={st.formInput}>
              {CUSTOMER_TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option> })}
            </select>
          </div>
          <div style={st.field}>
            <div style={st.formLabel}>POOL TYPE</div>
            <select value={selected.pool_type || ''} onChange={function(e) { updateCustomer({ pool_type: e.target.value || null }) }} style={st.formInput}>
              {POOL_TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option> })}
            </select>
          </div>
          <div style={st.field}>
            <div style={st.formLabel}>POOL SIZE</div>
            <input defaultValue={selected.pool_size || ''} onBlur={function(e) { if (e.target.value !== (selected.pool_size || '')) updateCustomer({ pool_size: e.target.value || null }) }} style={st.formInput} placeholder="e.g. 24ft round" />
          </div>
          <div style={st.field}>
            <div style={st.formLabel}>POOL INSTALL DATE</div>
            <input type="date" defaultValue={selected.pool_install_date || ''} onBlur={function(e) { if (e.target.value !== (selected.pool_install_date || '')) updateCustomer({ pool_install_date: e.target.value || null }) }} style={st.formInput} />
          </div>
        </div>

        {/* Tags */}
        <div style={{ marginTop: '14px' }}>
          <div style={st.formLabel}>TAGS</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginTop: '4px' }}>
            {(selected.tags || []).map(function(tag) {
              return <span key={tag} style={st.tag}>{tag}<button type="button" onClick={function() { removeTag(tag) }} style={st.tagX}>\u00D7</button></span>
            })}
            <input value={newTagInput} onChange={function(e) { setNewTagInput(e.target.value) }} onKeyDown={function(e) { if (e.key === 'Enter') { e.preventDefault(); addTag() } }} placeholder="Add tag + Enter" style={{ ...st.formInput, width: '160px', padding: '6px 10px', fontSize: '12px' }} />
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginTop: '14px' }}>
          <div style={st.formLabel}>NOTES {savingField === 'notes' && <span style={{ color: '#b48c3c', fontSize: '10px', marginLeft: '8px' }}>Saving...</span>}</div>
          <textarea value={notesDraft} onChange={function(e) { setNotesDraft(e.target.value) }} onBlur={saveNotes} style={{ ...st.formInput, minHeight: '70px', resize: 'vertical', fontFamily: "'Barlow',sans-serif" }} placeholder="Track status: called 4/15 — left voicemail. Following up Monday." />
        </div>

        {/* Detail tabs */}
        <div style={{ marginTop: '22px', display: 'flex', gap: '4px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          {[
            { id: 'services', label: 'SERVICE HISTORY (' + services.length + ')' },
            { id: 'requests', label: 'OPEN REQUESTS (' + openReqs.length + ')' },
            { id: 'emails', label: 'FOLLOW-UP HISTORY (' + emailLogs.length + ')' },
          ].map(function(t) {
            var active = detailTab === t.id
            return <button key={t.id} type="button" onClick={function() { setDetailTab(t.id) }} style={{ ...st.detailTabBtn, ...(active ? st.detailTabBtnActive : {}) }}>{t.label}</button>
          })}
        </div>

        <div style={{ marginTop: '16px' }}>
          {loadingDetail ? <div style={st.empty}>Loading...</div> : (
            <>
              {detailTab === 'services' && (
                <div>
                  <button type="button" onClick={openAddService} style={st.addBtnInline}>+ ADD SERVICE</button>
                  {services.length === 0 && <div style={st.empty}>No services logged yet.</div>}
                  {services.map(function(svc) {
                    return (
                      <div key={svc.id} style={st.serviceCard}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{svc.service_type}{svc.service_date ? ' \u2014 ' + fmtDate(svc.service_date) : ''}</div>
                          {svc.description && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '3px' }}>{svc.description}</div>}
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>
                            {[svc.pool_size, svc.pool_type, svc.installer].filter(Boolean).join(' \u00B7 ')}
                          </div>
                          {svc.notes && <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', marginTop: '4px', fontStyle: 'italic' }}>{svc.notes}</div>}
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          {svc.total_amount != null && <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '16px', fontWeight: 700, color: '#b48c3c' }}>{fmtMoney(svc.total_amount)}</div>}
                          <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', color: svc.status === 'completed' ? '#4a8c6a' : svc.status === 'scheduled' ? '#4a6a8c' : '#b48c3c', marginTop: '3px' }}>{(svc.status || '').toUpperCase()}</div>
                          <div style={{ display: 'flex', gap: '4px', marginTop: '6px', justifyContent: 'flex-end' }}>
                            <button type="button" onClick={function() { openEditService(svc) }} style={st.smBtn}>EDIT</button>
                            <button type="button" onClick={function() { deleteService(svc) }} style={st.smBtnDanger}>DEL</button>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}

              {detailTab === 'requests' && (
                <div>
                  {openReqs.length === 0 && <div style={st.empty}>No open requests for this customer.</div>}
                  {openReqs.map(function(r) {
                    var color = r.status === 'pending' ? '#b48c3c' : r.status === 'accepted' ? '#4a8c6a' : r.status === 'declined' ? '#e05c5c' : 'rgba(255,255,255,0.3)'
                    return (
                      <div key={r.id} style={{ ...st.serviceCard, borderLeft: '3px solid ' + color }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '14px', fontWeight: 600, color: '#ffffff' }}>{(r.pool_type || 'Request').replace(/_/g, ' ')}{r.pool_size ? ' \u2014 ' + r.pool_size : ''}</div>
                          {r.notes && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)', marginTop: '3px', whiteSpace: 'pre-wrap' }}>{r.notes}</div>}
                          <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginTop: '4px' }}>Submitted {fmtDate(r.created_at)}{r.preferred_date ? ' \u00B7 preferred ' + fmtDate(r.preferred_date) : ''}</div>
                          {r.decline_reason && <div style={{ fontSize: '11px', color: '#e05c5c', marginTop: '4px' }}>Declined: {r.decline_reason}</div>}
                        </div>
                        <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', color: color }}>{(r.status || '').toUpperCase()}</div>
                      </div>
                    )
                  })}
                </div>
              )}

              {detailTab === 'emails' && (
                <div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                    {selected.client_email && <button type="button" onClick={function() { openEmail(selected) }} style={st.addBtnInline}>+ SEND FOLLOW-UP</button>}
                    {selected.client_phone && <button type="button" onClick={function() { openInSmsApp(selected) }} style={st.addBtnInline}>+ SEND TEXT</button>}
                  </div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginBottom: '10px', lineHeight: 1.5 }}>
                    Shown below are follow-ups you composed. Actual send depends on your email or messaging app \u2014 check your Sent folder to confirm.
                  </div>
                  {emailLogs.length === 0 && <div style={st.empty}>No follow-ups logged for this customer yet.</div>}
                  {emailLogs.map(function(log) {
                    var method = log.send_method || 'server'
                    var methodLabel = method === 'mailto' ? 'Sent via email app' : method === 'sms' ? 'Sent via text app' : 'Sent via server'
                    return (
                      <details key={log.id} style={st.emailLogCard}>
                        <summary style={{ cursor: 'pointer', fontSize: '13px', color: '#ffffff' }}>
                          {log.subject || '(no subject)'}
                          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginLeft: '8px' }}>{methodLabel} \u00B7 {fmtDate(log.sent_at)}</span>
                        </summary>
                        <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255,255,255,0.55)', whiteSpace: 'pre-wrap' }}>{log.body}</div>
                      </details>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {/* Service modal */}
        {showService && (
          <div style={st.modalOverlay} onClick={function() { if (!serviceSaving) { setShowService(null) } }}>
            <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
              <div style={st.modalTitle}>{showService.mode === 'edit' ? 'EDIT SERVICE' : 'ADD SERVICE'}</div>
              <div style={st.infoGrid}>
                <div style={st.field}>
                  <div style={st.formLabel}>SERVICE TYPE</div>
                  <select value={serviceForm.service_type || ''} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { service_type: e.target.value })) }} style={st.formInput}>
                    {SERVICE_TYPES.map(function(t) { return <option key={t} value={t}>{t}</option> })}
                  </select>
                </div>
                <div style={st.field}>
                  <div style={st.formLabel}>SERVICE DATE</div>
                  <input type="date" value={serviceForm.service_date || ''} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { service_date: e.target.value })) }} style={st.formInput} />
                </div>
                <div style={st.field}>
                  <div style={st.formLabel}>POOL TYPE</div>
                  <select value={serviceForm.pool_type || ''} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { pool_type: e.target.value })) }} style={st.formInput}>
                    {POOL_TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option> })}
                  </select>
                </div>
                <div style={st.field}>
                  <div style={st.formLabel}>POOL SIZE</div>
                  <input value={serviceForm.pool_size || ''} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { pool_size: e.target.value })) }} style={st.formInput} placeholder="e.g. 24ft round" />
                </div>
                <div style={st.field}>
                  <div style={st.formLabel}>INSTALLER</div>
                  <input value={serviceForm.installer || ''} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { installer: e.target.value })) }} style={st.formInput} placeholder="Name of installer" />
                </div>
                <div style={st.field}>
                  <div style={st.formLabel}>TOTAL AMOUNT</div>
                  <input type="number" step="0.01" value={serviceForm.total_amount || ''} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { total_amount: e.target.value })) }} style={st.formInput} placeholder="0.00" />
                </div>
                <div style={st.field}>
                  <div style={st.formLabel}>STATUS</div>
                  <select value={serviceForm.status || 'completed'} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { status: e.target.value })) }} style={st.formInput}>
                    {SERVICE_STATUSES.map(function(s) { return <option key={s} value={s}>{s}</option> })}
                  </select>
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <div style={st.formLabel}>DESCRIPTION</div>
                <textarea value={serviceForm.description || ''} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { description: e.target.value })) }} style={{ ...st.formInput, minHeight: '60px', resize: 'vertical' }} placeholder="What was done" />
              </div>
              <div style={{ marginTop: '10px' }}>
                <div style={st.formLabel}>NOTES</div>
                <textarea value={serviceForm.notes || ''} onChange={function(e) { setServiceForm(Object.assign({}, serviceForm, { notes: e.target.value })) }} style={{ ...st.formInput, minHeight: '50px', resize: 'vertical' }} />
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button type="button" disabled={serviceSaving} onClick={function() { setShowService(null) }} style={st.ghostBtn}>CANCEL</button>
                <button type="button" disabled={serviceSaving} onClick={saveService} style={st.primaryBtn}>{serviceSaving ? 'SAVING...' : (showService.mode === 'edit' ? 'SAVE' : 'ADD')}</button>
              </div>
            </div>
          </div>
        )}

        {/* Email compose modal — hands off to the user's own email app */}
        {showEmail && (
          <div style={st.modalOverlay} onClick={function() { if (!emailOpening) setShowEmail(null) }}>
            <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
              <div style={st.modalTitle}>SEND FOLLOW-UP EMAIL</div>
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginBottom: '12px' }}>
                To: <span style={{ color: '#ffffff' }}>{showEmail.client_email}</span>
              </div>
              <div style={st.field}>
                <div style={st.formLabel}>SUBJECT</div>
                <input value={emailSubject} onChange={function(e) { setEmailSubject(e.target.value) }} style={st.formInput} />
              </div>
              <div style={{ marginTop: '10px' }}>
                <div style={st.formLabel}>MESSAGE</div>
                <textarea value={emailBody} onChange={function(e) { setEmailBody(e.target.value) }} style={{ ...st.formInput, minHeight: '160px', resize: 'vertical', lineHeight: 1.5 }} />
              </div>
              <div style={{ marginTop: '12px', fontSize: '11px', color: 'rgba(255,255,255,0.35)', lineHeight: 1.5 }}>
                This will open your default email app with the message ready to send. The email comes from your own address \u2014 your customer will reply directly to you.
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <button type="button" disabled={emailOpening} onClick={function() { setShowEmail(null) }} style={st.ghostBtn}>CANCEL</button>
                <button type="button" disabled={emailOpening} onClick={openInEmailApp} style={st.primaryBtn}>{String.fromCodePoint(0x2709, 0xFE0F) + ' OPEN IN EMAIL APP'}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // === LIST VIEW ===
  return (
    <div>
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '12px' }}>
        <button type="button" onClick={function() { setShowAdd(true); setAddForm({ customer_type: 'customer' }); setAddError(null) }} style={st.primaryBtn}>+ ADD CUSTOMER</button>
        <input value={search} onChange={function(e) { setSearch(e.target.value) }} placeholder="Search name, phone, email, address..." style={{ ...st.formInput, flex: 1, minWidth: '200px', maxWidth: '400px' }} />
        <select value={sortBy} onChange={function(e) { setSortBy(e.target.value) }} style={{ ...st.formInput, width: 'auto' }}>
          <option value="recent">Recently Added</option>
          <option value="contact">Last Contacted</option>
          <option value="name">A \u2013 Z</option>
        </select>
      </div>

      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginBottom: '16px' }}>
        {[
          { id: 'all', label: 'All' },
          { id: 'active', label: 'Active' },
          { id: 'prospects', label: 'Prospects' },
          { id: 'past', label: 'Past Customers' },
          { id: 'open_requests', label: 'With Open Requests' },
        ].map(function(f) {
          var active = filter === f.id
          return <button key={f.id} type="button" onClick={function() { setFilter(f.id) }} style={{ ...st.chip, ...(active ? st.chipActive : {}) }}>{f.label}</button>
        })}
      </div>

      {visibleCustomers.length === 0 ? (
        <div style={st.empty}>{loading ? 'Loading customers\u2026' : 'No customers match this view.'}</div>
      ) : (
        <div>
          {visibleCustomers.map(function(c) {
            var openReqs = getOpenRequests(c)
            return (
              <div key={c.id} style={st.listCard} onClick={function() { setSelectedId(c.id) }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: '#ffffff' }}>{c.client_name}</div>
                    {openReqs.length > 0 && <span style={st.openBadge}>OPEN REQUEST{openReqs.length > 1 ? 'S ' + openReqs.length : ''}</span>}
                    {c.customer_type && c.customer_type !== 'customer' && <span style={st.typeBadge}>{(c.customer_type || '').toUpperCase()}</span>}
                  </div>
                  <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '3px' }}>
                    {[c.client_phone, c.client_email].filter(Boolean).join(' \u00B7 ')}
                  </div>
                  {c.site_address && <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', marginTop: '2px' }}>{c.site_address}</div>}
                  {(c.tags || []).length > 0 && <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>{c.tags.map(function(t) { return <span key={t} style={st.tagSm}>{t}</span> })}</div>}
                </div>
                <div style={{ textAlign: 'right', fontSize: '11px', color: 'rgba(255,255,255,0.3)' }}>
                  {c.last_contact_date ? 'Last contact: ' + fmtDate(c.last_contact_date) : 'Never contacted'}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Add modal */}
      {showAdd && (
        <div style={st.modalOverlay} onClick={function() { if (!addSaving) setShowAdd(false) }}>
          <div style={st.modal} onClick={function(e) { e.stopPropagation() }}>
            <div style={st.modalTitle}>ADD CUSTOMER</div>
            <div style={st.infoGrid}>
              <div style={{ ...st.field, gridColumn: '1 / -1' }}>
                <div style={st.formLabel}>FULL NAME *</div>
                <input value={addForm.client_name || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { client_name: e.target.value })) }} autoFocus style={st.formInput} placeholder="First Last" />
              </div>
              <div style={st.field}><div style={st.formLabel}>PHONE</div><input value={addForm.client_phone || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { client_phone: e.target.value })) }} style={st.formInput} /></div>
              <div style={st.field}><div style={st.formLabel}>EMAIL</div><input value={addForm.client_email || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { client_email: e.target.value })) }} style={st.formInput} /></div>
              <div style={{ ...st.field, gridColumn: '1 / -1' }}><div style={st.formLabel}>ADDRESS</div><input value={addForm.site_address || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { site_address: e.target.value })) }} style={st.formInput} /></div>
              <div style={st.field}>
                <div style={st.formLabel}>CUSTOMER TYPE</div>
                <select value={addForm.customer_type || 'customer'} onChange={function(e) { setAddForm(Object.assign({}, addForm, { customer_type: e.target.value })) }} style={st.formInput}>
                  {CUSTOMER_TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option> })}
                </select>
              </div>
              <div style={st.field}>
                <div style={st.formLabel}>POOL TYPE</div>
                <select value={addForm.pool_type || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { pool_type: e.target.value })) }} style={st.formInput}>
                  {POOL_TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option> })}
                </select>
              </div>
              <div style={st.field}><div style={st.formLabel}>POOL SIZE</div><input value={addForm.pool_size || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { pool_size: e.target.value })) }} style={st.formInput} placeholder="e.g. 24ft round" /></div>
              <div style={st.field}><div style={st.formLabel}>POOL INSTALL DATE</div><input type="date" value={addForm.pool_install_date || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { pool_install_date: e.target.value })) }} style={st.formInput} /></div>
              <div style={{ ...st.field, gridColumn: '1 / -1' }}><div style={st.formLabel}>TAGS (comma-separated)</div><input value={addForm.tags_input || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { tags_input: e.target.value })) }} style={st.formInput} placeholder="VIP, Referral, Pool Store Regular" /></div>
              <div style={{ ...st.field, gridColumn: '1 / -1' }}><div style={st.formLabel}>NOTES</div><textarea value={addForm.notes || ''} onChange={function(e) { setAddForm(Object.assign({}, addForm, { notes: e.target.value })) }} style={{ ...st.formInput, minHeight: '60px', resize: 'vertical' }} /></div>
            </div>
            {addError && <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'rgba(224,92,92,0.12)', border: '1px solid rgba(224,92,92,0.3)', borderRadius: '3px', color: '#e05c5c', fontSize: '12px' }}>{addError}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              <button type="button" disabled={addSaving} onClick={function() { setShowAdd(false) }} style={st.ghostBtn}>CANCEL</button>
              <button type="button" disabled={addSaving} onClick={handleAddCustomer} style={st.primaryBtn}>{addSaving ? 'SAVING...' : 'ADD CUSTOMER'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

var st = {
  empty: { padding: '32px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '13px' },
  listCard: { display: 'flex', gap: '16px', alignItems: 'flex-start', padding: '14px 18px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '4px', cursor: 'pointer' },
  openBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', fontWeight: 700, letterSpacing: '1.5px', color: '#4a8c6a', backgroundColor: 'rgba(74,140,106,0.15)', border: '1px solid rgba(74,140,106,0.3)', padding: '2px 7px', borderRadius: '3px' },
  typeBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '9px', fontWeight: 600, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.4)', border: '1px solid rgba(255,255,255,0.15)', padding: '2px 7px', borderRadius: '3px' },
  chip: { padding: '6px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '16px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  chipActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  primaryBtn: { padding: '10px 18px', backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer' },
  ghostBtn: { padding: '10px 18px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  addBtnInline: { padding: '8px 14px', backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '3px', color: '#b48c3c', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 700, letterSpacing: '1.5px', cursor: 'pointer', marginBottom: '10px' },
  smBtn: { padding: '4px 8px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.6)', fontSize: '10px', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  smBtnDanger: { padding: '4px 8px', backgroundColor: 'rgba(224,92,92,0.1)', border: '1px solid rgba(224,92,92,0.25)', borderRadius: '3px', color: '#e05c5c', fontSize: '10px', fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  backBtn: { padding: '8px 14px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer', marginBottom: '16px' },
  detailHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.06)', flexWrap: 'wrap' },
  detailName: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '24px', fontWeight: 700, letterSpacing: '1.5px', color: '#ffffff' },
  contactLink: { color: '#b48c3c', textDecoration: 'none', fontSize: '13px' },
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' },
  field: {},
  formLabel: { display: 'block', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  formInput: { width: '100%', padding: '9px 12px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', boxSizing: 'border-box' },
  tag: { display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', backgroundColor: 'rgba(180,140,60,0.15)', border: '1px solid rgba(180,140,60,0.3)', borderRadius: '12px', color: '#b48c3c', fontSize: '11px', fontWeight: 600 },
  tagX: { background: 'none', border: 'none', color: '#b48c3c', cursor: 'pointer', padding: '0 2px', fontSize: '14px', lineHeight: 1 },
  tagSm: { padding: '2px 8px', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px solid rgba(180,140,60,0.25)', borderRadius: '10px', color: '#b48c3c', fontSize: '10px' },
  detailTabBtn: { padding: '10px 18px', background: 'none', border: 'none', borderBottom: '2px solid transparent', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  detailTabBtnActive: { color: '#b48c3c', borderBottomColor: '#b48c3c' },
  serviceCard: { display: 'flex', gap: '14px', padding: '12px 16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '4px' },
  emailLogCard: { padding: '10px 14px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px', marginBottom: '4px' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '24px' },
  modal: { backgroundColor: '#0f1420', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '6px', padding: '28px', width: '100%', maxWidth: '640px', maxHeight: '90vh', overflow: 'auto' },
  modalTitle: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '18px', fontWeight: 700, letterSpacing: '2px', color: '#ffffff', marginBottom: '16px' },
}
